import React, { useEffect, useState } from "react";
import { useInfiniteQuery, useQueryClient } from "react-query";
import { useOpticsStore } from "../../../stores/use-optics-store";
import { PaginationTypes } from "@Shared/Pagination-Types";
import NewsPreview from "../preview/NewsPreview";
import { useNewsStore } from "../../../stores/use-news-store";
import { searchBlogposts } from "../../../api/blog-api";
import { mapBlogpost } from "../useBlogs";
import CustomEmptyState from "../../../components/empty-state/CustomEmptyState";
import { CUSTOM_EMPTY_STATE_KEY } from "../../../components/empty-state/custom-templates";

function DynamicNews({ mode }) {
  const [page, setPage] = useState(0);
  const [atlassianContext, setAtlassianContext] = useState();

  const { config, selectedTab } = useNewsStore((state) => ({
    config: state.data.config,
    selectedTab: state.getSelectedTab(),
  }));

  const isCosmosInstalled = useOpticsStore((state) => state.isCosmosInstalled);

  const queryClient = useQueryClient();

  useEffect(() => {
    async function fetchData() {
      const context = await window.AP.context.getContext();
      setAtlassianContext(context);
    }
    fetchData();
  }, []);

  const { tabs, variant, pagination, size } = config || {};
  const { limit } = tabs[selectedTab] || {};

  const { isFetching, fetchNextPage, hasNextPage, data } = useInfiniteQuery(
    [
      "blogposts",
      {
        labels: config.tabs[selectedTab].labels,
        contributors: config.tabs[selectedTab].contributors,
        spaces: config.tabs[selectedTab].spaces,
        limit:
          config.variant !== "magazine" ? config.tabs[selectedTab].limit : 4,
        sorting: config.tabs[selectedTab].sorting,
        ordering: config.tabs[selectedTab].ordering,
        currentSpaceKey: atlassianContext?.confluence?.space?.key,
      },
    ],
    searchBlogposts,
    {
      retry: 0,
      select: (response) => {
        if (!response?.pages?.length) return undefined;

        const { pages } = response;

        const builtData = pages.reduce((acc, page, index) => {
          const body = JSON.parse(page.body);
          const blogs =
            body?.results?.map((blog) =>
              mapBlogpost(blog, isCosmosInstalled)
            ) || [];

          return {
            ...acc,
            [index]: blogs,
          };
        }, {});

        return builtData;
      },
      getNextPageParam: (lastPage) => {
        const body = JSON.parse(lastPage.body);

        if (body._links.next) {
          const urlParams = new URLSearchParams(encodeURI(body._links.next));
          const cursor = urlParams.get("cursor");
          return cursor;
        }
        return false;
      },
    }
  );

  useEffect(() => {
    queryClient.resetQueries({ queryKey: "blog" });
    setPage(0);
  }, [queryClient, pagination, variant, size, limit, selectedTab]);

  const queryPagination = {
    disabled: config.pagination === PaginationTypes.NONE,
    hasNextPage: hasNextPage || Object.keys(data || {})?.length - 1 > page,
    hasPrevPage: page >= 1,
    onNext: () => {
      if (isFetching) return;
      if (hasNextPage && Object.keys(data || {})?.length - 1 === page) {
        fetchNextPage();
      }
      if (hasNextPage || Object.keys(data || {})?.length - 1 > page) {
        setPage(page + 1);
      }
    },
    onPrev: () => {
      if (page >= 1) {
        setPage(page - 1);
      }
    },
    page,
    setPage,
  };

  if (!Object.values(data || {})?.[0]?.length) {
    return (
      <CustomEmptyState
        templateKey={CUSTOM_EMPTY_STATE_KEY.NEWS_EMPTY_RESULTS}
      />
    );
  }

  return (
    <NewsPreview data={data} queryPagination={queryPagination} mode={mode} />
  );
}

export default DynamicNews;
